import {
  createFileRoute,
  redirect,
  useNavigate,
  useRouteContext,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { addDays } from "date-fns";
import { getDefaultStore, useAtom, useAtomValue } from "jotai";
import { useCallback, useMemo, useRef, useState, type JSX } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import Down from "@repo/icons/default/Down";
import LocationMarker from "@repo/icons/default/LocationMarker";

import { CpsSpinner } from "corpus";
import { cn } from "@repo/cn";
import { DayAvailabilitySlots } from "@/components/onboarding/day-availability-slots";
import { RoundedLayout } from "@/components/ui/layout/rounded-layout";
import { WelcomeMeetingFooter } from "@/components/onboarding/welcome-meeting-footer";
import { DatePicker } from "@/components/ui/calendar/date-picker";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { Page, PageDescription, PageTitle } from "@/components/ui/layout/page/page";
import { CardSelectRoot } from "@/components/ui/select-input/card-select";
import { useGraphQL, useGraphQLMutationWithErrorHandler } from "@/hooks/use-graphql";
import type { WelcomeMeetingTimeSlot } from "@/lib/atoms/welcome-meeting-atom";
import {
  CONSULTING_DURATION_IN_MINUTES,
  WELCOME_MEETING_DURATION_IN_MINUTES,
  welcomeMeetingAtom,
} from "@/lib/atoms/welcome-meeting-atom";
import { consultingAtom } from "@/lib/atoms/consulting-atom";
import {
  buildConsultingMetadata,
  buildRespostasQualificacao,
} from "@/lib/consulting-metadata";
import { formatWithZonedDate, getCurrentDate } from "@/lib/helpers/date";

const WelcomeMeetingRescheduleMutation = graphql(`
  mutation WelcomeMeetingRescheduleMutation(
    $userId: Int!
    $visitType: String
    $visitTime: String!
    $unitId: Int!
    $durationInMinutes: Int!
    $sellerEmail: String
    $metadata: String
  ) {
    AnimusRescheduleSalesVisit(
      arg1: {
        userId: $userId
        visitType: $visitType
        visitTime: $visitTime
        unitId: $unitId
        durationInMinutes: $durationInMinutes
        sellerEmail: $sellerEmail
        metadata: $metadata
      }
    ) {
      message
    }
  }
`);

const WelcomeMeetingScheduleMutation = graphql(/* GraphQL */ `
  mutation WelcomeMeetingScheduleMutation(
    $date: String!
    $selectedLocationCode: Int!
    $origin: String!
    $durationInMinutes: Int!
    $userId: Int!
    $visitType: String
    $groupVisitScheduleId: Int
    $metadata: String
  ) {
    AnimusScheduleSalesVisit(
      arg1: {
        date: $date
        selectedLocationCode: $selectedLocationCode
        origin: $origin
        durationInMinutes: $durationInMinutes
        userId: $userId
        visitType: $visitType
        groupVisitScheduleId: $groupVisitScheduleId
        metadata: $metadata
      }
    ) {
      schedule
      blocking
    }
  }
`);

const ConsultingScheduleMetadataQuery = graphql(/* GraphQL */ `
  query ConsultingScheduleMetadataQuery($codFluxo: Int!) {
    usuarioPlano: usuariosProfissionaisPlanos(where: { ativo: { _eq: true } }) {
      plano {
        nomeExibicao
      }
    }
    membro {
      fluxos(where: { codFluxo: { eq: $codFluxo } }) {
        codFluxo
        etapas {
          codEtapa
          configuracoes
          resposta
        }
      }
    }
  }
`);

const DAYS_TO_FETCH = 5;

const COD_FLUXO_QUALIFICACAO = 1;

const scheduleSearchSchema = z.object({
  action: z.string().optional(),
  codUnidade: z.number().optional(),
  nome: z.string().optional(),
  endereco: z.string().optional(),
  consulting: z.boolean().optional(),
});

const WelcomeMeetingSchedulePage = (): JSX.Element => {
  const navigate = useNavigate();
  const router = useRouter();
  const { user } = useRouteContext({ from: "__root__" });
  const [form, setForm] = useAtom(welcomeMeetingAtom);
  const consultingForm = useAtomValue(consultingAtom);
  const searchParams = useSearch({ from: "/onboarding/welcome-meeting/schedule" });
  const isConsulting = searchParams.consulting === true;
  const durationInMinutes = isConsulting
    ? CONSULTING_DURATION_IN_MINUTES
    : WELCOME_MEETING_DURATION_IN_MINUTES;

  const { data: consultingMetadataData } = useGraphQL(
    ConsultingScheduleMetadataQuery,
    { codFluxo: COD_FLUXO_QUALIFICACAO },
    { enabled: isConsulting },
  );

  const { watch } = useForm({
    defaultValues: {
      selectedLocationCode: form.selectedLocationCode
        ? form.selectedLocationCode
        : (searchParams.codUnidade ?? 0),
      selectedLocationName: form.selectedLocationName
        ? form.selectedLocationName
        : (searchParams.nome ?? ""),
      selectedLocationAddress: form.selectedLocationAddress
        ? form.selectedLocationAddress
        : (searchParams.endereco ?? ""),
    },
  });

  const selectedLocationCode = watch("selectedLocationCode");
  const selectedLocationName = watch("selectedLocationName");
  const selectedLocationAddress = watch("selectedLocationAddress");

  if (
    form.selectedLocationCode === 0 &&
    selectedLocationCode !== 0 &&
    selectedLocationCode !== form.selectedLocationCode
  ) {
    setForm((prev) => ({
      ...prev,
      selectedLocationCode,
      selectedLocationName,
      selectedLocationAddress,
    }));
  }

  const today = getCurrentDate();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(today);

  const baseDate = selectedDate ?? today;

  const dateStrings = useMemo(
    () =>
      Array.from({ length: DAYS_TO_FETCH }, (_, i) =>
        formatWithZonedDate(addDays(baseDate, i), "yyyy-MM-dd"),
      ),
    [baseDate],
  );

  const especialidades = (user.especialidades ?? []) as {
    Especialidade?: { nome?: string };
  }[];
  const userSpecialty = especialidades[0]?.Especialidade?.nome ?? "";

  const baseVariables = {
    durationInMinutes,
    selectedLocationCode,
    specialty: userSpecialty,
    visitType: "onboarding",
  };

  const slotsRef = useRef(new Map<string, WelcomeMeetingTimeSlot>());
  const [dayStatuses, setDayStatuses] = useState<
    Map<string, { isLoading: boolean; slotCount: number }>
  >(new Map());

  const onStatusChange = useCallback(
    (date: string, status: { isLoading: boolean; slots: WelcomeMeetingTimeSlot[] }) => {
      for (const slot of status.slots) {
        slotsRef.current.set(slot.start, slot);
      }

      setDayStatuses((prev) => {
        const existing = prev.get(date);
        if (
          existing?.isLoading === status.isLoading &&
          existing.slotCount === status.slots.length
        ) {
          return prev;
        }
        const next = new Map(prev);
        next.set(date, { isLoading: status.isLoading, slotCount: status.slots.length });
        return next;
      });
    },
    [],
  );

  const isLoadingSlots = dateStrings.some((d) => dayStatuses.get(d)?.isLoading ?? true);
  const totalSlots = dateStrings.reduce(
    (sum, d) => sum + (dayStatuses.get(d)?.slotCount ?? 0),
    0,
  );
  const allDaysReported = dateStrings.every((d) => dayStatuses.has(d));

  const { mutate: rescheduleMeeting, isPending: isReschedulePending } =
    useGraphQLMutationWithErrorHandler(WelcomeMeetingRescheduleMutation);
  const { mutate: scheduleMeeting, isPending: isScheduling } =
    useGraphQLMutationWithErrorHandler(WelcomeMeetingScheduleMutation);

  const handleDateChange = (date: Date | undefined): void => {
    setSelectedDate(date);
    setForm((prev) => ({
      ...prev,
      date: "",
      timeSlot: null,
    }));
  };

  const handleSlotSelect = (value: string): void => {
    const slot = slotsRef.current.get(value);
    if (slot) {
      setForm((prev) => ({
        ...prev,
        date: slot.start,
        timeSlot: slot,
      }));
    }
  };

  const handleConfirm = (): void => {
    if (!form.timeSlot) return;

    const planos = consultingMetadataData?.usuarioPlano ?? [];
    const planoAtual = planos.length > 0 ? planos[0].plano.nomeExibicao : "";
    const etapasQualificacao = consultingMetadataData?.membro?.fluxos?.[0]?.etapas ?? [];

    const metadata = isConsulting
      ? buildConsultingMetadata(consultingForm, {
          respostasQualificacao: buildRespostasQualificacao(etapasQualificacao),
          planoAtual,
        })
      : undefined;
    const onScheduled = (): void => {
      void navigate({
        to: isConsulting
          ? "/onboarding/consulting/success"
          : "/onboarding/welcome-meeting/success",
      });
    };

    if (form.isRescheduling) {
      rescheduleMeeting(
        {
          userId: user.codUsuario,
          visitType: "onboarding",
          visitTime: form.timeSlot.start,
          unitId: form.selectedLocationCode,
          durationInMinutes,
          sellerEmail: user.email,
          metadata,
        },
        {
          onSuccess: onScheduled,
        },
      );
    } else {
      scheduleMeeting(
        {
          date: form.timeSlot.start,
          selectedLocationCode: form.selectedLocationCode,
          origin: "discovery",
          durationInMinutes,
          userId: user.codUsuario,
          visitType: "onboarding",
          groupVisitScheduleId: form.timeSlot.groupVisitScheduleId,
          metadata,
        },
        {
          onSuccess: onScheduled,
        },
      );
    }
  };

  return (
    <RoundedLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar progress={1} />
      </FlowHeader>
      <Page
        className="flex flex-col justify-between pb-0 px-6"
        showCustomerIoContainer={false}
      >
        <div className="flex flex-col gap-6">
          <div>
            {form.selectedLocationName ? (
              <button
                type="button"
                onClick={() => router.history.back()}
                className="flex items-center gap-1.5 self-start rounded-md border bg-neutral-50 px-4 py-2 text-md font-medium text-neutral-700"
              >
                <LocationMarker
                  size={24}
                  className="fill-primary-400"
                  aria-hidden="true"
                />
                {form.selectedLocationName}
                <Down size={24} aria-hidden="true" />
              </button>
            ) : null}

            <div>
              <PageTitle>Escolha o dia e horário para a reunião?</PageTitle>
              <PageDescription>
                Datas apresentadas com base na disponibilidade da unidade.
              </PageDescription>
            </div>
          </div>

          <DatePicker
            title="Agendas a partir de"
            value={selectedDate}
            onChange={handleDateChange}
            disabledPastDates={today}
          />

          {isLoadingSlots || !allDaysReported ? (
            <div className="flex justify-center items-center py-8 flex-1 h-24">
              <CpsSpinner small />
            </div>
          ) : null}

          <CardSelectRoot
            key={baseDate.toISOString()}
            name="welcome-meeting-time"
            defaultValue={form.timeSlot?.start}
            onChange={handleSlotSelect}
          >
            <div
              className={cn(
                "flex flex-col gap-6",
                (isLoadingSlots || !allDaysReported) && "hidden",
              )}
            >
              {dateStrings.map((date) => (
                <DayAvailabilitySlots
                  key={date}
                  date={date}
                  baseVariables={baseVariables}
                  onStatusChange={onStatusChange}
                />
              ))}
            </div>
          </CardSelectRoot>

          {!isLoadingSlots && allDaysReported && totalSlots === 0 ? (
            <p className="text-sm text-neutral-500 text-center py-8">
              Nenhum horário disponível nos próximos dias. Tente outra data.
            </p>
          ) : null}
        </div>

        <WelcomeMeetingFooter
          onSubmit={() => {
            handleConfirm();
          }}
          disabled={!form.timeSlot || isReschedulePending || isScheduling}
          loading={isReschedulePending || isScheduling}
        />
      </Page>
    </RoundedLayout>
  );
};

export const Route = createFileRoute("/onboarding/welcome-meeting/schedule")({
  validateSearch: scheduleSearchSchema,
  component: WelcomeMeetingSchedulePage,
  beforeLoad: ({ search }) => {
    const { selectedLocationCode } = getDefaultStore().get(welcomeMeetingAtom);

    if (selectedLocationCode === 0 && !search.codUnidade) {
      redirect({ to: "/onboarding/welcome-meeting/location", throw: true });
    }
  },
});
